import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { alpha, useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { axiosInstance } from '../services/axiosInstance';
import GatewayExeUpdateCard from '../components/system/GatewayExeUpdateCard';
import GatewayRemoteCard from '../components/system/GatewayRemoteCard';
import BrandManagerPoolAccessSection from '../components/system/BrandManagerPoolAccessSection';
import BrandManagerGatewayStreamMonitorSection from '../components/system/BrandManagerGatewayStreamMonitorSection';
import { useMenuPolicy } from '../brand/MenuPolicyContext';
import {
  GRADES,
  Grade,
  Grants,
  MENU_ITEMS,
  MenuItem,
  applyToggle,
  defaultGrants,
} from '../brand/menuCatalog';

function readApiError(err: any, fallback: string): string {
  const data = err?.response?.data;
  const detail = data?.detail ?? data?.message;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter((item) => typeof item === 'string' && item.trim());
    if (msgs.length) return msgs.join(' ');
  }
  const status = err?.response?.status;
  if (status) return `${fallback} (${status})`;
  return fallback;
}

const GRADE_LABEL: Record<Grade, string> = {
  super: '최고관리자',
  manager: '일반관리자',
  facility: '시설',
};

const COLS = 'minmax(0, 1fr) 104px 104px 88px';

function childrenOf(parentId: string): MenuItem[] {
  return MENU_ITEMS.filter((item) => item.parentId === parentId);
}

function descendantCount(id: string): number {
  const kids = childrenOf(id);
  return kids.reduce((sum, kid) => sum + 1 + descendantCount(kid.id), 0);
}

function gradeColor(theme: { palette: { primary: { main: string }; success: { main: string }; warning: { main: string } } }, grade: Grade): string {
  if (grade === 'super') return theme.palette.primary.main;
  if (grade === 'manager') return theme.palette.success.main;
  return theme.palette.warning.main;
}

function GradeChecks({
  item,
  draft,
  onToggle,
}: {
  item: MenuItem;
  draft: Grants;
  onToggle: (id: string, grade: Grade, checked: boolean) => void;
}) {
  const theme = useTheme();
  return (
    <>
      {GRADES.map((grade) => (
        <Box key={grade} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Checkbox
            checked={Boolean(draft[item.id]?.[grade])}
            onChange={(e) => onToggle(item.id, grade, e.target.checked)}
            inputProps={{ 'aria-label': `${item.label} ${GRADE_LABEL[grade]}` }}
            sx={{
              color: alpha(gradeColor(theme, grade), 0.7),
              '&.Mui-checked': { color: gradeColor(theme, grade) },
            }}
          />
        </Box>
      ))}
    </>
  );
}

function GradeTitles() {
  const theme = useTheme();
  return (
    <>
      {GRADES.map((grade) => (
        <Typography
          key={grade}
          sx={{
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: gradeColor(theme, grade),
          }}
        >
          {GRADE_LABEL[grade]}
        </Typography>
      ))}
    </>
  );
}

function ChildRows({
  parentId,
  draft,
  onToggle,
}: {
  parentId: string;
  draft: Grants;
  onToggle: (id: string, grade: Grade, checked: boolean) => void;
}) {
  const theme = useTheme();
  const items = childrenOf(parentId);

  return (
    <>
      {items.map((item) => {
        const kids = childrenOf(item.id);
        const grouped = kids.length > 0;
        return (
          <Box
            key={item.id}
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: grouped ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.035) : 'transparent',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: COLS,
                alignItems: 'center',
                minHeight: 52,
                px: 2,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography sx={{ fontSize: 15, fontWeight: grouped ? 700 : 500 }}>{item.label}</Typography>
              <GradeChecks item={item} draft={draft} onToggle={onToggle} />
            </Box>
            {kids.map((kid) => (
              <Box
                key={kid.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  alignItems: 'center',
                  minHeight: 48,
                  px: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography sx={{ fontSize: 15, pl: 2.5, color: 'text.primary' }}>{kid.label}</Typography>
                <GradeChecks item={kid} draft={draft} onToggle={onToggle} />
              </Box>
            ))}
          </Box>
        );
      })}
    </>
  );
}

function MenuCard({
  item,
  draft,
  onToggle,
}: {
  item: MenuItem;
  draft: Grants;
  onToggle: (id: string, grade: Grade, checked: boolean) => void;
}) {
  const theme = useTheme();
  const kids = childrenOf(item.id);
  const wide = kids.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        gridColumn: wide ? { md: '1 / -1' } : 'auto',
        minWidth: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: COLS,
              alignItems: 'center',
              px: 2,
              py: 1.75,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
              borderBottom: kids.length ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box
                sx={{
                  width: 4,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.35 }}>{item.label}</Typography>
                {kids.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    하위 메뉴 {descendantCount(item.id)}개
                  </Typography>
                )}
              </Box>
            </Box>
            {GRADES.map((grade) => (
              <Box key={grade} sx={{ textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: gradeColor(theme, grade),
                    lineHeight: 1.2,
                  }}
                >
                  {GRADE_LABEL[grade]}
                </Typography>
                <Checkbox
                  checked={Boolean(draft[item.id]?.[grade])}
                  onChange={(e) => onToggle(item.id, grade, e.target.checked)}
                  inputProps={{ 'aria-label': `${item.label} ${GRADE_LABEL[grade]}` }}
                  sx={{
                    color: alpha(gradeColor(theme, grade), 0.7),
                    '&.Mui-checked': { color: gradeColor(theme, grade) },
                  }}
                />
              </Box>
            ))}
          </Box>

          {kids.length > 0 && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  bgcolor: 'action.hover',
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>하위 메뉴</Typography>
                <GradeTitles />
              </Box>
              <ChildRows parentId={item.id} draft={draft} onToggle={onToggle} />
            </>
          )}
    </Paper>
  );
}

const BrandMenuPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { authState } = useAuth();
  const { grants, replaceGrants, refresh } = useMenuPolicy();
  const [draft, setDraft] = useState<Grants>(defaultGrants());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [archiveDeleteAllowed, setArchiveDeleteAllowed] = useState(false);
  const [archiveDeleteSaving, setArchiveDeleteSaving] = useState(false);
  const [archiveDeleteMessage, setArchiveDeleteMessage] = useState('');
  const [archiveDeleteError, setArchiveDeleteError] = useState('');
  const [shareImageEnabled, setShareImageEnabled] = useState(true);
  const [kakaoBotEnabled, setKakaoBotEnabled] = useState(false);
  const [notifyExtrasSaving, setNotifyExtrasSaving] = useState(false);
  const [notifyExtrasMessage, setNotifyExtrasMessage] = useState('');
  const [notifyExtrasError, setNotifyExtrasError] = useState('');
  const [purgeOnServer, setPurgeOnServer] = useState(false);
  const [purgeTargets, setPurgeTargets] = useState<string[]>([]);
  const [purgePassword, setPurgePassword] = useState('');
  const [purgePasswordAgain, setPurgePasswordAgain] = useState('');
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeMessage, setPurgeMessage] = useState('');
  const [purgeError, setPurgeError] = useState('');
  const [purgeOpen, setPurgeOpen] = useState(false);

  useEffect(() => {
    setDraft(grants);
  }, [grants]);

  useEffect(() => {
    if (!authState.user?.is_brand_manager) return;
    let cancelled = false;
    void axiosInstance.get('/api/brand-menu/archive-delete').then(({ data }) => {
      if (!cancelled) setArchiveDeleteAllowed(Boolean(data?.allow_delete));
    }).catch(() => {
      if (!cancelled) setArchiveDeleteError('삭제 허용 상태 조회 실패');
    });
    return () => {
      cancelled = true;
    };
  }, [authState.user?.is_brand_manager]);

  useEffect(() => {
    if (!authState.user?.is_brand_manager) return;
    let cancelled = false;
    void axiosInstance
      .get('/api/brand-menu/event-notify-extras')
      .then(({ data }) => {
        if (cancelled) return;
        setShareImageEnabled(Boolean(data?.event_share_image_enabled));
        setKakaoBotEnabled(Boolean(data?.kakao_bot_send_enabled));
      })
      .catch(() => {
        if (!cancelled) setNotifyExtrasError('알림 부가 기능 상태 조회 실패');
      });
    return () => {
      cancelled = true;
    };
  }, [authState.user?.is_brand_manager]);

  useEffect(() => {
    if (!authState.user?.is_brand_manager) return;
    let cancelled = false;
    void axiosInstance.get('/api/brand-menu/purge-install').then(({ data }) => {
      if (cancelled) return;
      setPurgeOnServer(Boolean(data?.server));
      setPurgeTargets(Array.isArray(data?.targets) ? data.targets : []);
      if (!data?.server && data?.detail) setPurgeError(String(data.detail));
    }).catch(() => {
      if (!cancelled) setPurgeError('삭제 대상 조회 실패');
    });
    return () => {
      cancelled = true;
    };
  }, [authState.user?.is_brand_manager]);

  const saveArchiveDelete = async (next: boolean) => {
    setArchiveDeleteAllowed(next);
    setArchiveDeleteSaving(true);
    setArchiveDeleteMessage('');
    setArchiveDeleteError('');
    try {
      const { data } = await axiosInstance.put('/api/brand-menu/archive-delete', { allow_delete: next });
      setArchiveDeleteAllowed(Boolean(data?.allow_delete));
      setArchiveDeleteMessage(next ? '관제 자료실 삭제 허용 켬' : '관제 자료실 삭제 허용 끔');
    } catch (err: any) {
      setArchiveDeleteAllowed(!next);
      setArchiveDeleteError(readApiError(err, '삭제 허용 저장 실패'));
    } finally {
      setArchiveDeleteSaving(false);
    }
  };

  const saveNotifyExtras = async (
    patch: Partial<{ event_share_image_enabled: boolean; kakao_bot_send_enabled: boolean }>
  ) => {
    const prevShare = shareImageEnabled;
    const prevBot = kakaoBotEnabled;
    if (typeof patch.event_share_image_enabled === 'boolean') {
      setShareImageEnabled(patch.event_share_image_enabled);
    }
    if (typeof patch.kakao_bot_send_enabled === 'boolean') {
      setKakaoBotEnabled(patch.kakao_bot_send_enabled);
    }
    setNotifyExtrasSaving(true);
    setNotifyExtrasMessage('');
    setNotifyExtrasError('');
    try {
      const { data } = await axiosInstance.put('/api/brand-menu/event-notify-extras', patch);
      setShareImageEnabled(Boolean(data?.event_share_image_enabled));
      setKakaoBotEnabled(Boolean(data?.kakao_bot_send_enabled));
      setNotifyExtrasMessage('알림 부가 기능 저장 완료');
    } catch (err: any) {
      setShareImageEnabled(prevShare);
      setKakaoBotEnabled(prevBot);
      setNotifyExtrasError(readApiError(err, '알림 부가 기능 저장 실패'));
    } finally {
      setNotifyExtrasSaving(false);
    }
  };

  const purgeInstall = async () => {
    setPurgeMessage('');
    setPurgeError('');
    if (!purgePassword || purgePassword !== purgePasswordAgain) {
      setPurgeError('확인 비밀번호 불일치');
      return;
    }
    if (!window.confirm('소스 폴더와 프로그램 폴더를 삭제. 되돌릴 수 없음.')) return;
    setPurgeBusy(true);
    try {
      await axiosInstance.post('/api/brand-menu/purge-install', {
        password: purgePassword,
        password_confirm: purgePasswordAgain,
      });
      setPurgePassword('');
      setPurgePasswordAgain('');
      setPurgeMessage('삭제 시작. 잠시 후 폴더가 사라지고 서버가 멈춤.');
    } catch (err: any) {
      setPurgeError(readApiError(err, '폴더 삭제 실패'));
    } finally {
      setPurgeBusy(false);
    }
  };

  if (!authState.loading && !authState.user?.is_brand_manager) {
    return <Navigate to={authState.user?.is_superuser ? '/central-control' : '/individual-monitoring'} replace />;
  }

  const onToggle = (id: string, grade: Grade, checked: boolean) => {
    setDraft((prev) => applyToggle(prev, id, grade, checked));
    setMessage('');
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const { data } = await axiosInstance.put('/api/brand-menu/policy', { grants: draft });
      if (data?.grants) replaceGrants(data.grants as Grants);
      else await refresh();
      setMessage('메뉴 허용 저장 완료. 체크된 항목만 해당 등급에 표시.');
    } catch (err: any) {
      setError(readApiError(err, '저장 실패'));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    setPasswordMessage('');
    setPasswordError('');
    if (newPassword !== confirmPassword) {
      setPasswordError('새 비밀번호 확인 불일치');
      return;
    }
    try {
      await axiosInstance.post('/api/brand-menu/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('비밀번호 변경 완료. 다음 로그인부터 새 비밀번호 사용.');
    } catch (err: any) {
      setPasswordError(readApiError(err, '비밀번호 변경 실패'));
    }
  };

  const adminMenus = MENU_ITEMS.filter((item) => item.parentId === null && item.id !== 'facility.root');
  const facilityMenu = MENU_ITEMS.find((item) => item.id === 'facility.root');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto', pb: 10 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.25 }}>메뉴 허용</Typography>
            <Typography sx={{ mt: 0.75, fontSize: 15, color: 'text.secondary', maxWidth: 760, lineHeight: 1.6 }}>
              체크된 항목만 해당 등급 화면에 표시. 시설 체크 시 일반관리자·최고관리자도 함께 체크. 최고관리자 해제 시 일반관리자·시설도 함께 해제.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/central-control')} sx={{ flexShrink: 0 }}>
            중앙 제어로
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {GRADES.map((grade) => (
            <Box
              key={grade}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: 10,
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha(gradeColor(theme, grade), 0.12),
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: gradeColor(theme, grade) }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{GRADE_LABEL[grade]}</Typography>
            </Box>
          ))}
        </Box>

        <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1.25 }}>비밀번호 변경</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
            <TextField
              size="small"
              type="password"
              label="현재 비밀번호"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <TextField
              size="small"
              type="password"
              label="새 비밀번호"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              size="small"
              type="password"
              label="새 비밀번호 확인"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button variant="outlined" onClick={changePassword}>
              변경
            </Button>
          </Box>
          {passwordMessage && <Alert severity="success" sx={{ mt: 1.5 }}>{passwordMessage}</Alert>}
          {passwordError && <Alert severity="error" sx={{ mt: 1.5 }}>{passwordError}</Alert>}
        </Paper>

        <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>관제 자료실 삭제 허용</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1 }}>
            이 계정만 변경 가능. 켜면 관제 자료실에서 글·첨부 삭제 가능.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={archiveDeleteAllowed}
                disabled={archiveDeleteSaving}
                onChange={(e) => void saveArchiveDelete(e.target.checked)}
              />
            }
            label={archiveDeleteAllowed ? '허용' : '잠금'}
          />
          {archiveDeleteMessage && <Alert severity="success" sx={{ mt: 1.5 }}>{archiveDeleteMessage}</Alert>}
          {archiveDeleteError && <Alert severity="error" sx={{ mt: 1.5 }}>{archiveDeleteError}</Alert>}
        </Paper>

        <BrandManagerPoolAccessSection />

        <BrandManagerGatewayStreamMonitorSection />

        <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>이벤트 알림 부가 기능</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1 }}>
            시스템 관리 「알림」과 동일. 카카오 봇=번호 검색 후 이미지 자동 전송(테스트). 메뉴 트리는 등급별 표시용.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={shareImageEnabled}
                disabled={notifyExtrasSaving}
                onChange={(e) =>
                  void saveNotifyExtras({ event_share_image_enabled: e.target.checked })
                }
              />
            }
            label="합성 이미지 서비스"
          />
          <FormControlLabel
            control={
              <Switch
                checked={kakaoBotEnabled}
                disabled={notifyExtrasSaving}
                onChange={(e) =>
                  void saveNotifyExtras({ kakao_bot_send_enabled: e.target.checked })
                }
              />
            }
            label="카카오 봇 보내기"
          />
          {notifyExtrasMessage && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              {notifyExtrasMessage}
            </Alert>
          )}
          {notifyExtrasError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {notifyExtrasError}
            </Alert>
          )}
        </Paper>

        <Box sx={{ mb: 3 }}>
          <GatewayExeUpdateCard />
        </Box>
        <GatewayRemoteCard />

        <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1.5 }}>관리자 메뉴</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {adminMenus.map((item) => (
            <MenuCard key={item.id} item={item} draft={draft} onToggle={onToggle} />
          ))}
        </Box>

        {facilityMenu && (
          <Box sx={{ mt: 3.5 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>시설 메뉴</Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5 }}>
              시설 화면 메뉴. 체크된 항목만 해당 등급에 표시.
            </Typography>
            <MenuCard item={facilityMenu} draft={draft} onToggle={onToggle} />
          </Box>
        )}

        <Paper
          elevation={6}
          sx={{
            position: 'sticky',
            bottom: 16,
            mt: 3,
            px: 2,
            py: 1.5,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 2,
            zIndex: 5,
          }}
        >
          <Box sx={{ flex: '1 1 280px' }}>
            {message && <Alert severity="success">{message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            {!message && !error && (
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                이 계정은 체크와 관계없이 전체 메뉴 표시.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => {
                setDraft(defaultGrants());
                setMessage('');
              }}
            >
              처음 화면 기준으로 되돌리기
            </Button>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? '저장 중' : '저장'}
            </Button>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ mt: 3, mb: 2, border: 1, borderColor: 'error.main', borderRadius: 2, overflow: 'hidden' }}>
          <Box
            onClick={() => setPurgeOpen((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {purgeOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>프로그램 폴더 삭제</Typography>
          </Box>
          <Collapse in={purgeOpen} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1 }}>
                서버에서만 실행. 로컬 PC에서는 삭제하지 않음. Pack-And-Send가 서버에 두는
                /home/계정/ansim-kyma, /opt/libexec/rtd, 압축을 풀어 둔 /tmp 폴더,
                DB 데이터·로그·접속 설정(/var/lib/postgresql, /var/log/postgresql, /etc/postgresql)을 삭제.
                되돌릴 수 없음. 확인 비밀번호 두 번.
              </Typography>
              {purgeOnServer && purgeTargets.length > 0 ? (
                <Box sx={{ mb: 1.25 }}>
                  {purgeTargets.map((target) => (
                    <Typography key={target} sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {target}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1 }}>
                  {purgeOnServer ? '삭제 대상 폴더 없음' : '이 화면은 로컬. 서버에 반영한 뒤 그 서버에서 실행.'}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
                <TextField
                  size="small"
                  type="password"
                  label="확인 비밀번호"
                  value={purgePassword}
                  autoComplete="new-password"
                  onChange={(e) => setPurgePassword(e.target.value)}
                />
                <TextField
                  size="small"
                  type="password"
                  label="확인 비밀번호 다시"
                  value={purgePasswordAgain}
                  autoComplete="new-password"
                  onChange={(e) => setPurgePasswordAgain(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="error"
                  disabled={purgeBusy || !purgeOnServer || purgeTargets.length === 0}
                  onClick={() => void purgeInstall()}
                >
                  {purgeBusy ? '삭제 중' : '폴더 삭제'}
                </Button>
              </Box>
              {purgeMessage && <Alert severity="success" sx={{ mt: 1.5 }}>{purgeMessage}</Alert>}
              {purgeError && <Alert severity="error" sx={{ mt: 1.5 }}>{purgeError}</Alert>}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    </Box>
  );
};

export default BrandMenuPage;
